import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { categories, featuredComics, latestUpdates, hotRankings } from '@/mocks/comics';

function ScrollToTopHandler() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return null;
}

export default function Home() {
  const { t } = useTranslation();

  return (
    <>
      <ScrollToTopHandler />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dynamic%20Japanese%20manga%20art%20style%20collage%20with%20action%20scenes%20and%20vibrant%20characters%2C%20warm%20red%20and%20orange%20color%20palette%2C%20comic%20reading%20atmosphere%2C%20abstract%20energy%20waves%2C%20cinematic%20lighting%2C%20dramatic%20composition%2C%20modern%20illustration%20style%2C%20no%20text&width=1600&height=900&seq=bika-hero-bg&orientation=landscape"
            alt="哔咔动漫"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70" />
          <div className="relative z-10 w-full px-4 md:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-black text-white mb-4 leading-tight">
                哔咔动漫
              </h1>
              <p className="text-base md:text-xl text-white/80 mb-8 leading-relaxed max-w-xl mx-auto">
                免费全集 | 哔咔漫画免费观看下拉式在线阅读 - 日漫韩漫全免费
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/riman"
                  className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('home.hero.cta')}
                </Link>
                <Link
                  to="#hot-recommend"
                  className="px-8 py-3 rounded-full bg-white/15 text-white font-medium text-sm hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm border border-white/20"
                >
                  {t('home.hero.secondaryCta')}
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5">
              <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {t('home.categories.title')}
              </h2>
              <p className="text-sm text-foreground-500">{t('home.categories.subtitle')}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat, idx) => {
                const icons = ['ri-sun-line', 'ri-moon-line', 'ri-flag-2-line', 'ri-check-double-line', 'ri-loader-4-line'];
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer border border-transparent hover:border-primary-200/50"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors">
                      <i className={`${icons[idx]} text-xl md:text-2xl text-primary-600`} />
                    </div>
                    <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500 text-center hidden sm:block">
                      {cat.description.slice(0, 10)}...
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Hot Recommend Section */}
        <section id="hot-recommend" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {t('home.hotRecommend.title')}
                </h2>
                <p className="text-sm text-foreground-500">{t('home.hotRecommend.subtitle')}</p>
              </div>
              <Link
                to="/category/riman"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                {t('common.viewAll')} <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Update Section */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {t('home.latestUpdate.title')}
                </h2>
                <p className="text-sm text-foreground-500">{t('home.latestUpdate.subtitle')}</p>
              </div>
              <Link
                to="/category/lianzai"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                {t('common.viewAll')} <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {latestUpdates.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Rankings Section */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {t('home.rankings.title')}
              </h2>
              <p className="text-sm text-foreground-500">{t('home.rankings.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              {hotRankings.map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-4 p-3 md:p-4 rounded-lg bg-background-50 hover:bg-primary-50/50 transition-colors cursor-pointer group"
                >
                  <span className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                    idx < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="w-10 h-14 md:w-12 md:h-16 rounded overflow-hidden flex-shrink-0 bg-background-200">
                    <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" width="48" height="64" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-500 flex-shrink-0">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500" />
                      {comic.rating}
                    </span>
                    <span className="hidden sm:inline">{comic.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {t('home.features.title')}
              </h2>
              <p className="text-sm text-foreground-500">{t('home.features.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {(t('home.features.items', { returnObjects: true }) as { icon: string; title: string; desc: string }[]).map((item, idx) => (
                <div key={idx} className="text-center p-6 md:p-8 rounded-xl bg-background-100">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100">
                    <i className={`${item.icon} text-2xl text-accent-600`} />
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-bold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full px-4 md:px-8 py-20 md:py-28 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20toned%20abstract%20geometric%20pattern%20with%20soft%20gradients%20of%20coral%20and%20amber%2C%20modern%20minimal%20design%2C%20smooth%20flowing%20shapes%2C%20reading%20and%20entertainment%20atmosphere%2C%20cozy%20inviting%20feel%2C%20no%20text%20or%20people&width=1600&height=600&seq=bika-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
              {t('home.cta.title')}
            </h2>
            <p className="text-base text-white/70 mb-8 leading-relaxed">
              {t('home.cta.subtitle')}
            </p>
            <Link
              to="/category/riman"
              className="inline-flex px-8 py-3.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('home.cta.button')}
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "哔咔动漫",
            url: "https://bikadongman.com/",
            description: "哔咔动漫免费全集在线下拉阅读，日漫韩漫国漫海量资源无付费限制。提供哔咔漫画免费观看完整入口。",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://bikadongman.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "哔咔动漫 · 免费漫画天堂",
            description: "哔咔动漫免费全集在线下拉阅读，日漫韩漫国漫海量资源无付费限制。",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://bikadongman.com/" },
              ],
            },
          }),
        }}
      />
    </>
  );
}